/*#
# Palette

A standard set of board colors so boards are told apart at a glance, the way
Trello backgrounds do it. Each color has a background, a slightly deeper
shade for lane headers, and an ink that reads on both. `boardColor(name)`
looks one up; `colorFor(text)` picks one deterministically from any string,
so a lane or a person gets the same color every time without anyone
choosing it.

```js
import { PALETTE, colorFor } from 'tosijs-virta'
import { elements } from 'tosijs'
const { div, span } = elements

preview.append(
  div({ style: { display: 'flex', gap: '6px', flexWrap: 'wrap' } },
    ...Object.entries(PALETTE).map(([name, c]) =>
      span({ style: { background: c.bg, color: c.ink, padding: '8px 12px', borderRadius: '8px', fontWeight: 600 } }, name)
    )
  ),
  div({ style: { marginTop: '8px' } }, `colorFor('tosijs-ui') → ${colorFor('tosijs-ui').name}`)
)
```
```test
test('eight swatches render, each painted', () => {
  const swatches = Array.from(preview.querySelectorAll('span'))
  expect(swatches.length).toBe(8)
  expect(swatches.every((s) => s.style.background !== '')).toBe(true)
})
```
*/

/*{ "parent": "Reference", "order": 15 }*/

export interface BoardColor {
  name: string
  /** board background */
  bg: string
  /** lane headers, selected states */
  deep: string
  /** text that reads on `bg` and `deep` */
  ink: string
}

const define = (
  name: string,
  bg: string,
  deep: string,
  ink: string
): BoardColor => ({ name, bg, deep, ink })

/** Eight named board colors. Stable order; add at the end. */
export const PALETTE: Record<string, BoardColor> = {
  sky: define('sky', '#2f6fd6', '#245bb3', '#ffffff'),
  teal: define('teal', '#1f8f86', '#197570', '#ffffff'),
  moss: define('moss', '#4c8a3a', '#3f7330', '#ffffff'),
  amber: define('amber', '#d98b1c', '#b87316', '#1c1200'),
  coral: define('coral', '#d9553d', '#b64632', '#ffffff'),
  plum: define('plum', '#7a4bb8', '#653d99', '#ffffff'),
  slate: define('slate', '#546678', '#455563', '#ffffff'),
  stone: define('stone', '#8a7f72', '#72695e', '#ffffff'),
}

export const PALETTE_NAMES = Object.keys(PALETTE)

/** Named color, or `slate` for anything unknown (including ''). */
export function boardColor(name: string | null | undefined): BoardColor {
  return (
    (name && Object.hasOwn(PALETTE, name) && PALETTE[name]) || PALETTE.slate!
  )
}

/** FNV-1a; small and stable across runtimes. */
export function hashText(text: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h
}

/** A stable palette color for any string (lane value, identity, tag). */
export function colorFor(text: string): BoardColor {
  return PALETTE[PALETTE_NAMES[hashText(text) % PALETTE_NAMES.length]!]!
}

/** The palette color after the one whose background is `bg` (the first, for anything else). */
export function nextColor(bg: string): BoardColor {
  const i = PALETTE_NAMES.findIndex((n) => PALETTE[n]!.bg === bg)
  return PALETTE[PALETTE_NAMES[(i + 1) % PALETTE_NAMES.length]!]!
}

/**
 * Black or white, whichever reads better on a `#rgb` / `#rrggbb` background
 * (WCAG relative luminance); `undefined` for anything else — a gradient or
 * an image has no one luminance to measure.
 */
export function inkOn(bg: string): string | undefined {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(bg.trim())
  if (!m) return undefined
  const hex =
    m[1]!.length === 3 ? m[1]!.replace(/./g, (c) => c + c) : (m[1] as string)
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
  const l = 0.2126 * r + 0.7152 * g + 0.0722 * b
  // contrast against white vs against black: pick the larger
  return 1.05 / (l + 0.05) >= (l + 0.05) / 0.05 ? '#ffffff' : '#000000'
}
